import type { ScenarioComparison, ScenarioResult } from '@projections/engine';
import { useMemo } from 'react';
import { formatCompactCurrency, formatCurrency, formatPercent } from '../design/format';
import { EChart } from './EChart';
import { type ProjectionTokens, resolveTokens } from './theme';

/**
 * STRESS-SCENARIO COMPARISON chart (PLAN.md M7) — categorical hues with the BASELINE as a neutral
 * reference. Compares the baseline against each named stress scenario (retire-into-YYYY, worst/best-
 * first) on a comparable headline metric (success probability or comparable terminal wealth), as a
 * bar chart. The baseline bar is `--neutral`; each scenario takes a categorical hue (`--cat-*`), and
 * a dashed `markLine` at the baseline value renders every scenario as a delta against it.
 *
 * Consumes the engine's `ScenarioComparison` (`{ baseline, scenarios[] }`) from `runStressComparison`.
 */
export interface StressComparisonChartProps {
  comparison: ScenarioComparison;
  /** Which metric to plot. `success` = successProbability; `terminal` = comparable terminal wealth. */
  metric?: 'success' | 'terminal';
  height?: number;
}

export function StressComparisonChart({
  comparison,
  metric = 'success',
  height = 320,
}: StressComparisonChartProps): React.ReactElement {
  const option = useMemo(() => buildStressOption(comparison, metric), [comparison, metric]);
  return <EChart option={option} height={height} aria-label="Stress scenario comparison" />;
}

/** Read the comparable metric value off a `ScenarioResult`. */
function metricValue(s: ScenarioResult, metric: 'success' | 'terminal'): number {
  return metric === 'success' ? s.successProbability : s.terminalWealth;
}

/**
 * Build the stress-comparison ECharts option. PURE — exported for unit testing.
 *
 * x-axis = [baseline.name, ...scenarios.name]; a single bar series of the chosen metric, with the
 * baseline bar coloured `--neutral` and each scenario a `--cat-*` hue via per-datum `itemStyle`. A
 * dashed `markLine` at the baseline value lets each scenario read as a delta. Success-axis labels
 * format as percent; terminal-axis labels format as compact ฿.
 *
 * `tokens` defaults to the live design-system tokens (`resolveTokens()`); tests pass a fixture so
 * the option shape can be asserted with no DOM.
 */
export function buildStressOption(
  comparison: ScenarioComparison,
  metric: 'success' | 'terminal',
  tokens: ProjectionTokens = resolveTokens(),
): Record<string, unknown> {
  const scenarios = [comparison.baseline, ...comparison.scenarios];
  const categories = scenarios.map((s) => s.name);
  const baselineValue = metricValue(comparison.baseline, metric);

  const data = scenarios.map((s, i) => {
    // Baseline = neutral; scenarios cycle the categorical palette.
    const color =
      i === 0 ? tokens.neutral : tokens.categorical[(i - 1) % tokens.categorical.length];
    return {
      value: metricValue(s, metric),
      itemStyle: { color, borderRadius: [2, 2, 0, 0] },
    };
  });

  const isSuccess = metric === 'success';
  const axisFormatter = (v: number): string =>
    isSuccess ? formatPercent(v, 0) : formatCompactCurrency(v);
  const fullFormatter = (v: number): string => (isSuccess ? formatPercent(v) : formatCurrency(v));

  return {
    animation: true,
    animationDuration: 600,
    grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: unknown) => {
        const arr = Array.isArray(params) ? params : [params];
        const first = arr[0] as { dataIndex?: number; axisValue?: string } | undefined;
        const i = first?.dataIndex ?? -1;
        const s = scenarios[i];
        if (s === undefined) return '';
        const value = metricValue(s, metric);
        const delta = value - baselineValue;
        const deltaStr =
          i === 0
            ? 'baseline'
            : `${delta >= 0 ? '+' : '−'}${fullFormatter(Math.abs(delta))} vs baseline`;
        const dd = s.worstDrawdown;
        const ddStr =
          dd && dd.maxDrawdown > 0
            ? `<div style="color:var(--ink-muted)">worst drawdown ${formatPercent(dd.maxDrawdown)} (${dd.troughYear})</div>`
            : '';
        return [
          `<div style="font-weight:600;margin-bottom:4px">${s.name}</div>`,
          `<div>${isSuccess ? 'success' : 'terminal'}&nbsp;&nbsp;${fullFormatter(value)}</div>`,
          `<div style="color:var(--ink-muted)">${deltaStr}</div>`,
          ddStr,
        ].join('');
      },
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { interval: 0, hideOverlap: true, fontSize: 10 },
    },
    yAxis: {
      type: 'value',
      ...(isSuccess ? { min: 0, max: 1 } : {}),
      axisLabel: { formatter: axisFormatter },
    },
    series: [
      {
        name: isSuccess ? 'Success probability' : 'Terminal wealth',
        type: 'bar',
        data,
        barMaxWidth: 48,
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: tokens.neutral, type: 'dashed', width: 1 },
          label: {
            formatter: `baseline ${axisFormatter(baselineValue)}`,
            color: tokens.inkMuted,
            fontFamily: tokens.fontMono,
            fontSize: 11,
            // Left-anchored so the label sits in the gap before the first bar instead of being
            // clipped at the right grid edge.
            position: 'insideStartTop',
          },
          data: [{ yAxis: baselineValue }],
        },
      },
    ],
  };
}
